class AudioTrackPlayer {
    constructor() {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.buffer = null;
        this.gainNode = this.audioContext.createGain();
        this.gainNode.connect(this.audioContext.destination);
        this.sourceNode = null;
        this.isPlaying = false;
        this.startTime = 0; // context time when playback started
        this.pauseOffset = 0; // offset in seconds from the start of the audio
        this.listener = null;
        this.intervalId = null;
        this.silentAudio = new Audio();
        this.silentAudio.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==";
    }

    setListener(listener) {
        this.listener = listener;
        if (this.listener && !this.intervalId) {
            this._startInterval();
        } else if (!this.listener && this.intervalId) {
            this._stopInterval();
        }
    }

    _startInterval() {
        if (this.intervalId) return;
        this.intervalId = setInterval(() => {
            if (this.listener) {
                this.listener(this.getState());
            }
        }, 100);
    }

    _stopInterval() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    async loadTrack(url, volume) {
        this.release();
        try {
            console.log(`[AudioTrackPlayer] Fetching: ${url}`);
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            this.buffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.gainNode.gain.value = volume ?? 1.0;
        } catch (e) {
            console.error(`[AudioTrackPlayer] Failed to load track from ${url}`, e);
        }
    }

    play() {
        if (this.isPlaying || !this.buffer) return;
        this.silentAudio.play().catch(e => console.log("Silent audio failed", e));
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        const now = this.audioContext.currentTime;
        this.startTime = now - this.pauseOffset;

        this._startSource(this.pauseOffset);
        this.isPlaying = true;
    }

    _startSource(offset) {
        if (this.sourceNode) {
            this.sourceNode.onended = null;
            this.sourceNode.stop();
        }
        this.sourceNode = this.audioContext.createBufferSource();
        this.sourceNode.buffer = this.buffer;
        this.sourceNode.connect(this.gainNode);

        this.sourceNode.onended = () => {
            if (this.isPlaying) {
                // If it ended naturally (not via stop/pause)
                this.isPlaying = false;
                this.pauseOffset = this.buffer.duration;
                if (this.listener) {
                    this.listener(this.getState());
                }
            }
        };

        // Ensure offset doesn't exceed duration
        const startOffset = Math.max(0, Math.min(offset, this.buffer.duration));
        this.sourceNode.start(0, startOffset);
    }

    pause() {
        if (!this.isPlaying) return;

        this.pauseOffset = this.audioContext.currentTime - this.startTime;
        if (this.sourceNode) {
            this.sourceNode.onended = null;
            this.sourceNode.stop();
            this.sourceNode = null;
        }

        this.isPlaying = false;
    }

    seekTo(timeMs) {
        const wasPlaying = this.isPlaying;
        if (wasPlaying) {
            this.pause();
        }

        this.pauseOffset = timeMs / 1000;

        if (this.buffer) {
            this.pauseOffset = Math.max(0, Math.min(this.pauseOffset, this.buffer.duration));
        }

        if (wasPlaying) {
            this.play();
        }
    }

    setVolume(volume) {
        this.gainNode.gain.value = volume;
    }

    release() {
        this._stopInterval();
        this.pause();
        this.buffer = null;
        this.pauseOffset = 0;
        this.listener = null;
    }

    getState() {
        const duration = this.buffer ? this.buffer.duration : 0;
        const currentTime = this.isPlaying
            ? (this.audioContext.currentTime - this.startTime)
            : this.pauseOffset;

        return {
            isPlaying: this.isPlaying,
            currentTimeMs: Math.round(currentTime * 1000),
            durationMs: Math.round(duration * 1000),
        };
    }
}

export default AudioTrackPlayer;
